<script setup lang="ts">
import { onBeforeUnmount, nextTick } from "vue";
import { locale } from "../../locale";
import { dragState, dropFile } from "../../states/chatFiles";
import ChatMessagesArea from "../chat/ChatMessagesArea.vue";
import InputArea from "../chat/InputArea.vue";
import {
  isSavedScrollPosition,
  mounted,
  restoreScrollPosition,
  saveScrollPosition,
  toBottomScrollChat,
} from "../../states/scrollChatState";
import {
  errorChatState,
  getInitialMessages,
  loadingChatState,
  messagesList,
} from "../../states/chatState";
import { waitLoading } from "../../states/loadingMessageImage";
import { readMarkEl } from "../../states/readMarkState";
import ReloadIcon from "../chat/ReloadIcon.vue";
import LoadingIcon from "../chat/LoadingIcon.vue";
import { currentUploadFile, previewImageUrl } from "../../states/chatFiles";

const supportsWebkitGetAsEntry =
  "webkitGetAsEntry" in DataTransferItem.prototype;

async function drop(ev: DragEvent) {
  if (!supportsWebkitGetAsEntry) {
    dropFile(ev.dataTransfer?.files.item(0));
  } else if (ev.dataTransfer) {
    const fileHandles = [...ev.dataTransfer.items]
      .filter((item) => item.kind === "file")
      .map((item) => item.webkitGetAsEntry());
    for (const handle of fileHandles) {
      if (!handle) continue;
      if (handle.isDirectory) {
        console.log("drop directory not supported");
      } else if (handle.isFile) {
        const h = handle as FileSystemFileEntry;
        h.file((f) => {
          dropFile(f);
        });
      }
    }
  }
}

const scrollReady = () => {
  nextTick(async () => {
    if (readMarkEl.value && !mounted.value) {
      readMarkEl.value.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (isSavedScrollPosition()) {
      restoreScrollPosition();
    } else {
      toBottomScrollChat();
    }
    await Promise.all(
      messagesList.value
        .filter((m) => m.type === "File" && m.content.file?.preview)
        .map((m) => {
          if ("id" in m) return waitLoading(m.id as string);
          else return Promise.resolve();
        }),
    );
    if (readMarkEl.value && !mounted.value) {
      readMarkEl.value.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (isSavedScrollPosition()) {
      restoreScrollPosition();
    } else {
      toBottomScrollChat();
    }
    mounted.value = true;
  });
};

onBeforeUnmount(() => {
  saveScrollPosition();
});
function reloadChat() {
  getInitialMessages();
}
</script>
<template>
  <div class="room-chat">
    <div v-if="errorChatState.startMessages" class="error-start-messages">
      <div class="inner">
        <span>{{ locale.chat.errorGetStartMessages }}</span>
        <div class="loading" v-if="loadingChatState.startMessages">
          <LoadingIcon />
        </div>
        <div class="reload" v-else @click="reloadChat">
          <ReloadIcon />
        </div>
      </div>
    </div>
    <div
      v-else-if="dragState"
      class="drop-zone"
      @dragover.prevent="() => {}"
      @drop.prevent="drop"
    >
      <div ref="dropZone" class="dashed">
        <span>{{ locale.chat.drop }}</span>
      </div>
    </div>
    <div class="room-chat-block">
      <div class="chat-area">
        <div v-if="previewImageUrl || currentUploadFile" class="blur"></div>
        <ChatMessagesArea @scrollready="scrollReady" />
      </div>
      <div class="input-area">
        <InputArea />
      </div>
    </div>
  </div>
</template>
<style scoped>
.room-chat {
  width: 100%;
  height: 100%;
  background-color: #1e1818;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.close-chat-btn {
  position: absolute;
  right: 4px;
  top: 8px;
  cursor: pointer;
}

.clear-chat-btn {
  position: absolute;
  left: 8px;
  top: 8px;
  cursor: pointer;
}

.title {
  height: 40px;
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #333030;
  color: #f6f6f6;
}

.title .text {
  font: Open Sans;
  font-size: 14px;
  font-weight: 600;
}

.room-chat-block {
  height: 100%;
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.chat-area {
  width: 100%;
  height: 100px;
  flex-grow: 1;
  position: relative;
}

.blur {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: rgba(30, 24, 24, 0.9);
  backdrop-filter: blur(4px);
}

.drop-zone {
  position: absolute;
  z-index: 100;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  background-color: rgba(30, 24, 24, 0.9);
  backdrop-filter: blur(4px);
}

.error-start-messages {
  position: absolute;
  z-index: 100;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  background-color: rgba(30, 24, 24, 0.9);
  backdrop-filter: blur(4px);
}

.error-start-messages .inner {
  border-radius: 8px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 14px;
}

.dashed {
  border: 2px dashed rgba(116, 116, 116, 1);
  border-radius: 8px;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

.reload {
  cursor: pointer;
}
</style>
